/**
 * MixedActionTable — table view for mixed action types (including a
 * homogeneous single-action-type fast path for tables of 5+ similar events).
 *
 * Built on the shared `Table` / `TableCard` primitives from `@/lib/base/table`
 * so it stays visually consistent with the rest of the app's data tables and
 * picks up future component-library improvements for free.
 */

import { AlertTriangle } from '@untitledui/icons';
import { useMemo } from 'react';

import { StatusBanner } from '@/lib/base/banners';
import { Table, TableCard } from '@/lib/base/table';
import {
  getEntityTypeLabel,
  pluralizeEntityLabel,
} from '@/lib/crm/utils/entityTypeUtils';
import Pagination from '@/lib/proposals/Pagination';

import type {
  ProposalDetail,
  ProposedAction,
} from '../../lib/proposals/core/types';
import { isEditableAction } from '../../lib/proposals/registry/utils';
import type { SelectionState } from '../../lib/proposals/types/selection';
import { ACTION_TABLE_PAGE_SIZE } from '../../lib/proposals/utils/constants';
import { getStableActionKey } from '../../lib/proposals/utils/helpers';
import { MixedActionExpandedRow } from './MixedActionExpandedRow';
import {
  HomogeneousDataRow,
  MixedActionDataRow,
  MixedActionTableHeaderRow,
} from './MixedActionTable.helpers';
import { useMixedActionTableData } from './MixedActionTable.utils';
import {
  useTablePaginationState,
  useTableSelectionHandlers,
} from './tableHooks';

interface MixedActionTableProps {
  actions: ProposedAction[];
  proposal: ProposalDetail;
  onUpdate: (proposal: ProposalDetail) => void;
  isProcessing?: boolean;
  selectionState?: SelectionState;
  fillContainer?: boolean;
}

// ─── Main MixedActionTable component ──────────────────────────────────────

export function MixedActionTable({
  actions,
  proposal,
  onUpdate,
  isProcessing = false,
  selectionState,
  fillContainer = false,
}: MixedActionTableProps) {
  const {
    visibleActions,
    associationCountMap,
    isHomogeneous,
    entityTypeName,
    stableAssociationTypes,
    stableStageAssociationTypes,
    propertySchemaMap,
  } = useMixedActionTableData(actions, proposal);

  const {
    currentPage,
    paginatedActions,
    expandedActionKey,
    pendingEditActionKey,
    handlePageChange,
    handleRowClick,
    handleRowEditClick,
  } = useTablePaginationState(visibleActions);

  // Edit affordance removed from the per-row view — edits happen inside
  // the expanded row only, since the inline pencil in each table row added
  // visual noise without a clear benefit. Keeping the prop plumbing so the
  // row components can still render an empty placeholder if needed; this
  // constant turns off the edit column everywhere.
  const editColumnEnabled = false;

  const { handleToggle, handleHeaderCheckboxChange } =
    useTableSelectionHandlers(selectionState);

  const tableRows = useMemo(() => {
    const rows: Array<{
      type: 'data' | 'expanded';
      action: ProposedAction;
      id: string;
    }> = [];
    // Row ids become React keys, so they must survive a save. See
    // getStableActionKey — a save past the versioning window mints a new
    // action.id, and keying on it would remount the row (and any editor
    // inside the expanded row) mid-edit.
    for (const action of paginatedActions) {
      const stableId = getStableActionKey(action);
      rows.push({ type: 'data', action, id: String(stableId) });
      if (expandedActionKey === stableId) {
        rows.push({ type: 'expanded', action, id: `${stableId}-expanded` });
      }
    }
    return rows;
  }, [paginatedActions, expandedActionKey]);

  const lastExecutionFailed =
    proposal.resolution === 'pending' &&
    Boolean(proposal.last_execution_failed_at);

  if (visibleActions.length === 0) {
    return (
      <div className='p-6 text-center text-sm text-tertiary'>
        No actions to display
      </div>
    );
  }

  const showPagination = visibleActions.length > ACTION_TABLE_PAGE_SIZE;

  // colSpan for the expanded row. Must match the number of columns rendered
  // in the header for the current mode (homogeneous vs mixed).
  const totalColumns = isHomogeneous
    ? 1 + // chevron
      (selectionState ? 1 : 0) +
      1 + // status
      4 + // name, property, current, proposed
      (editColumnEnabled ? 1 : 0)
    : 1 + // chevron
      (selectionState ? 1 : 0) +
      1 + // status
      1 + // action
      (editColumnEnabled ? 1 : 0);

  const labelText =
    isHomogeneous && entityTypeName
      ? `${visibleActions.length} ${pluralizeEntityLabel(
          getEntityTypeLabel(entityTypeName).toLowerCase()
        )}`
      : `${visibleActions.length} actions`;
  let descriptionText: string;
  if (lastExecutionFailed) {
    descriptionText =
      "Some actions didn't go through. Review them below, then approve to retry.";
  } else if (isHomogeneous) {
    descriptionText = 'Will be updated when approved';
  } else {
    descriptionText = 'Will be executed when approved';
  }

  return (
    <>
      {lastExecutionFailed && (
        <div className='mb-4' data-testid='proposal-execution-failed-banner'>
          <StatusBanner
            variant='warning'
            icon={AlertTriangle}
            title="Last attempt didn't finish"
            description="Some actions weren't completed. Adjust or deselect the flagged actions, then approve to retry."
          />
        </div>
      )}
      {/* Homogeneous "5+ similar events" tables keep the narrow proposal-view
          width (matches the original non-shared Table layout). Mixed tables
          continue to honor the caller's fillContainer preference so they can
          span the full proposal width when needed. */}
      <TableCard.Root
        size='sm'
        fillContainer={fillContainer && !isHomogeneous}
        className={isHomogeneous ? 'mx-auto w-full max-w-[1000px]' : undefined}
      >
        <TableCard.Header title={labelText} description={descriptionText} />
        <TableCard.ScrollArea>
          <Table aria-label='Mixed action types' size='sm'>
            <MixedActionTableHeaderRow
              isHomogeneous={isHomogeneous}
              selectionState={selectionState}
              editColumnEnabled={editColumnEnabled}
              onHeaderCheckboxChange={handleHeaderCheckboxChange}
            />
            <Table.Body>
              {tableRows.map(row => {
                const { action, type } = row;
                const isSelected = selectionState
                  ? selectionState.isSelected(action.id)
                  : true;
                const isDisabled = selectionState
                  ? selectionState.isDisabled(action.id)
                  : false;
                const disabledReason = selectionState
                  ? selectionState.getDisabledReason(action.id)
                  : null;
                const isExpanded =
                  expandedActionKey === getStableActionKey(action);
                if (type === 'expanded') {
                  return (
                    <MixedActionExpandedRow
                      key={row.id}
                      action={action}
                      proposal={proposal}
                      onUpdate={onUpdate}
                      isProcessing={isProcessing}
                      totalColumns={totalColumns}
                      autoStartEdit={
                        pendingEditActionKey === getStableActionKey(action)
                      }
                      stageAssociationTypes={stableStageAssociationTypes}
                    />
                  );
                }
                const supportsEditing = isEditableAction(
                  action,
                  stableStageAssociationTypes
                );
                if (isHomogeneous) {
                  return (
                    <HomogeneousDataRow
                      key={row.id}
                      action={action}
                      isSelected={isSelected}
                      isDisabled={isDisabled}
                      disabledReason={disabledReason}
                      isExpanded={isExpanded}
                      onToggle={handleToggle}
                      onRowClick={handleRowClick}
                      selectionEnabled={!!selectionState}
                      orgId={proposal.org}
                      associationTypes={stableAssociationTypes}
                      stageAssociationTypes={stableStageAssociationTypes}
                      propertySchemaMap={propertySchemaMap}
                      editColumnEnabled={editColumnEnabled}
                      supportsEditing={supportsEditing}
                      onEditClick={handleRowEditClick}
                    />
                  );
                }
                return (
                  <MixedActionDataRow
                    key={row.id}
                    action={action}
                    isSelected={isSelected}
                    isDisabled={isDisabled}
                    disabledReason={disabledReason}
                    isExpanded={isExpanded}
                    onToggle={handleToggle}
                    onRowClick={handleRowClick}
                    selectionEnabled={!!selectionState}
                    orgId={proposal.org}
                    associationCount={associationCountMap.get(action.id) ?? 0}
                    editColumnEnabled={editColumnEnabled}
                    supportsEditing={supportsEditing}
                    onEditClick={handleRowEditClick}
                  />
                );
              })}
            </Table.Body>
          </Table>
        </TableCard.ScrollArea>
        {showPagination && (
          <TableCard.Footer aria-label='Table pagination' role='navigation'>
            <Pagination
              total={visibleActions.length}
              pageSize={ACTION_TABLE_PAGE_SIZE}
              page={currentPage}
              onPageChange={handlePageChange}
              className='!border-t-0'
            />
          </TableCard.Footer>
        )}
      </TableCard.Root>
    </>
  );
}
